// src/hooks/useDesignSettings.js
// ─────────────────────────────────────────────────────────────────────────────
// Reads design settings from platform_settings (category = 'design').
// Module-level cache with 5-minute TTL, one DB call shared across all consumers.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";

let _cache     = null;
let _cacheTime = 0;
const TTL      = 5 * 60 * 1000; // 5 minutes

export function useDesignSettings() {
  const [settings, setSettings] = useState(_cache || {});

  useEffect(() => {
    const now = Date.now();
    if (_cache && now - _cacheTime < TTL) {
      setSettings(_cache);
      return;
    }
    supabase
      .from("platform_settings")
      .select("key, value")
      .eq("category", "design")
      .then(({ data }) => {
        const s = {};
        (data || []).forEach((r) => { s[r.key] = r.value; });
        // Expose slider array for convenient consumption
        s.default_hero_slider = [1, 2, 3, 4]
          .map(n => s[`default_hero_slider_${n}`])
          .filter(Boolean);
        // Parse ordered slide list if saved by HeroSliderManager
        if (s.homepage_hero_slides) {
          try {
            const arr = JSON.parse(s.homepage_hero_slides);
            s.homepage_hero_slides_parsed = Array.isArray(arr) && arr.length ? arr : null;
          } catch { s.homepage_hero_slides_parsed = null; }
        }
        s.category_heroes = Object.fromEntries(
          Object.entries(s).filter(([k]) => k.startsWith('category_hero_'))
            .map(([k, v]) => [k.replace('category_hero_', ''), v])
        );
        _cache     = s;
        _cacheTime = Date.now();
        setSettings(s);
      });
  }, []);

  return settings;
}

// Call this after saving a design setting so the next read is fresh.
export function invalidateDesignCache() {
  _cache     = null;
  _cacheTime = 0;
}

// Call at app startup to warm the cache before any component needs it.
export function prefetchDesignSettings() {
  if (_cache && Date.now() - _cacheTime < TTL) return;
  supabase
    .from("platform_settings")
    .select("key, value")
    .eq("category", "design")
    .then(({ data }) => {
      const s = {};
      (data || []).forEach((r) => { s[r.key] = r.value; });
      s.default_hero_slider = [1, 2, 3, 4]
        .map(n => s[`default_hero_slider_${n}`])
        .filter(Boolean);
      if (s.homepage_hero_slides) {
        try {
          const arr = JSON.parse(s.homepage_hero_slides);
          s.homepage_hero_slides_parsed = Array.isArray(arr) && arr.length ? arr : null;
        } catch { s.homepage_hero_slides_parsed = null; }
      }
      s.category_heroes = Object.fromEntries(
        Object.entries(s).filter(([k]) => k.startsWith('category_hero_'))
          .map(([k, v]) => [k.replace('category_hero_', ''), v])
      );
      _cache     = s;
      _cacheTime = Date.now();
    });
}
